import { useTranslation } from 'react-i18next';
import { Col, Divider, Icon, Row, Text, Title } from 'ui';
import { useUser } from 'features/auth';
import { type BillingInformationValues, PayerInformation } from '../../../../../components';
import { useGivingCreate, useSubscriptionPlans } from '../../../../../hooks';
import { useGiveNow } from '../../../context';
import { GiveStepsEnum } from '../../types';
import { Footer } from '../footer';
import { GiveNowItem } from '../give-now-item';
import { GiveNowItemsWrapper } from '../give-now-items-wrapper';

type P = {
  billingInformation?: BillingInformationValues;
  isLoading: boolean;
  onContinue: () => void;
  setStep: (step: GiveStepsEnum) => void;
};

const Summary = ({ billingInformation, isLoading, onContinue, setStep }: P) => {
  const { railsUserData: viewer } = useUser();

  const { t } = useTranslation();
  const { payerInformation } = useGivingCreate();
  const { viewerItems } = useGiveNow();
  const { changeIntervalToText, getTranslate } = useSubscriptionPlans();
  const shouldDisplayOrganization =
    viewer.membershipsCount.joinedOrganizations && viewer.membershipsCount.joinedOrganizations > 1;
  const interval = viewerItems[0]?.recurring ?? 'one_time';

  return (
    <>
      <Row gutter={['ms', 0]}>
        <Col span={12}>
          <GiveNowItemsWrapper>
            {shouldDisplayOrganization ? (
              <Row className="give-now-summary-organization" gutter={['ms', 0]}>
                <Col>
                  <Icon icon="bank-line" size="medium-small" />
                </Col>
                <Col>
                  <Text weight="medium">{viewerItems[0]?.organization.label}</Text>
                </Col>
              </Row>
            ) : null}
            <Row align="middle" className="give-now-summary-interval" gutter={['ms', 0]}>
              <Col>
                <Icon icon="exchange-dollar-line" size="medium-small" />
              </Col>
              <Col>
                {interval === 'one_time' ? (
                  <>
                    <Text weight="medium">One time</Text>
                    <br />
                    <Text size="small">You will be charged only once</Text>
                  </>
                ) : (
                  <>
                    <Text weight="medium">{getTranslate(interval)}</Text>
                    <br />
                    <Text size="small">
                      {t('recurringPaymentInfo', {
                        interval: changeIntervalToText(interval),
                      })}
                    </Text>
                  </>
                )}
              </Col>
            </Row>
            <Divider marginNone />
            {viewerItems.map((item) => (
              <GiveNowItem giving={item} key={item.id} />
            ))}
          </GiveNowItemsWrapper>
        </Col>
        <Col span={12}>
          <Col span={24}>
            <Title level={4} marginBottom>
              Billing information
            </Title>
          </Col>
          <Col span={24}>
            <PayerInformation payer={payerInformation({ formValues: billingInformation })} />
          </Col>
        </Col>
      </Row>

      <Footer
        buttonHtmlType="button"
        isLoading={isLoading}
        nextButtonText="Continue to payment"
        onBack={() => {
          setStep(GiveStepsEnum.OPTIONS);
        }}
        onNext={onContinue}
      />
    </>
  );
};

export default Summary;
